import { useState, useEffect } from 'react'
import Button from 'aldoo-ra/Button'
import Typography from 'aldoo-ra/Typography'
import Modal from 'aldoo-ra/Modal'
import { GetCMSContent, SaveCMSContent } from 'aldoo-ra/CMS/api'
import { clearCMSCache } from 'aldoo-ra/CMS/cms-data'
import ContentSelectorDialog from 'aldoo-ra/CMS/dialogs/content-selector-dialog'
import ContentEditor from 'aldoo-ra/CMS/content-editor'
import TypeResolver from 'aldoo-ra/CMS/type-resolver'
import HtmlEditor from 'aldoo-ra/HtmlEditor'
import { showNotification } from 'aldoo-ra/Notification'

/**
 * HTMLContent Component Editor
 * Custom editor for HTMLContent component slots
 * Provides New/Load functionality and displays current article
 */
const HTMLContentEditor = ({ slot, onSave, onClose, onHeaderButtons }) => {
  const [currentArticle, setCurrentArticle] = useState(null)
  const [showSelector, setShowSelector] = useState(false)
  const [showHtmlEditor, setShowHtmlEditor] = useState(false)
  const [showContentEditor, setShowContentEditor] = useState(false)
  const [loading, setLoading] = useState(true)

  const { getContentType } = TypeResolver()
  const HTMLContentType = getContentType('HTMLContent')

  // Load current article if slot has data.id
  useEffect(() => {
    const loadCurrentArticle = async () => {
      const htmlContentId = slot?.content?.data?.id

      if (htmlContentId) {
        try {
          const response = await GetCMSContent({
            match: {
              'content.type': 'HTMLContent',
              'content.id': htmlContentId
            },
            itemsPerPage: 1
          })

          if (response.result && response.data && response.data.length > 0) {
            const article = response.data[0]
            setCurrentArticle(article)
          }
        } catch (error) {
          console.error('[HTMLContent Editor] Failed to load article:', error)
        }
      }
      setLoading(false)
    }

    loadCurrentArticle()
  }, [slot])

  // Provide header buttons to parent
  useEffect(() => {
    if (onHeaderButtons) {
      onHeaderButtons(
        <div className="flex gap-2 mr-12">
          <Button variant="admin_secondary" onClick={handleNew}>
            New
          </Button>
          <Button variant="admin_secondary" onClick={handleLoad}>
            Load
          </Button>
        </div>
      )
    }
  }, [onHeaderButtons])

  const handleNew = () => {
    setShowContentEditor(true)
  }

  const handleLoad = () => {
    setShowSelector(true)
  }

  const handleSelectArticle = (article) => {
    // Update slot data with selected article ID
    const updatedSlot = {
      ...slot,
      content: {
        ...slot.content,
        data: {
          ...slot.content?.data,
          id: article.content.id
        }
      }
    }

    setCurrentArticle(article)
    setShowSelector(false)

    // Save the updated slot
    if (onSave) {
      onSave(updatedSlot)
    }
  }

  const handleHtmlEditorSave = async (htmlContent) => {
    // The HtmlEditor's save prop receives the HTML content string
    // Update the current article with the new HTML in content.content field
    if (currentArticle) {
      const updatedArticle = {
        ...currentArticle,
        content: {
          ...currentArticle.content,
          content: htmlContent
        }
      }

      try {
        // Save to CMS with proper payload structure
        const savePayload = {
          _id: currentArticle._id,
          content: {
            ...currentArticle.content,
            content: htmlContent
          },
          createdAt: currentArticle.createdAt,
          deleted: currentArticle.deleted || false,
          localization: currentArticle.localization || []
        }

        const savedArticle = await SaveCMSContent({ payload: savePayload })

        // Clear the cache for HTMLContent to force refresh on next load
        clearCMSCache('HTMLContent')

        // Reload the article from the saved response to ensure we have the latest data
        if (savedArticle?.data) {
          setCurrentArticle(savedArticle.data)
        } else {
          setCurrentArticle(updatedArticle)
        }
      } catch (error) {
        console.error('[HTMLContent Editor] Failed to save article:', error)
        return // Don't close editor on error
      }
    }

    setShowHtmlEditor(false)
  }

  const handleArticleClick = () => {
    if (currentArticle) {
      setShowHtmlEditor(true)
    }
  }

  const handleContentEditorSave = async (fieldValues) => {
    // ContentEditor passes the field values, wrap them in content object
    const contentData = {
      ...fieldValues,
      type: 'HTMLContent'
    }

    const savePayload = {
      content: contentData
    }

    const response = await SaveCMSContent({ payload: savePayload })

    if (response.result) {
      showNotification({
        message: `HTML Content "${fieldValues.id}" created successfully`,
        className: 'bg-admin_primary text-admin_text_inv'
      })

      // Clear cache and load the new article
      clearCMSCache('HTMLContent')

      // Set as current article and update slot
      if (response.data) {
        // Update slot with new article ID
        const updatedSlot = {
          ...slot,
          content: {
            ...slot.content,
            data: {
              ...slot.content?.data,
              id: response.data.content.id
            }
          }
        }

        // Save the slot first, then update UI state
        if (onSave) {
          await onSave(updatedSlot)
        }

        // Set the current article after slot is saved
        setCurrentArticle(response.data)

        // Automatically open HTML editor for the newly created content
        setShowContentEditor(false)
        setShowHtmlEditor(true)
      } else {
        setShowContentEditor(false)
      }

      return response
    } else {
      showNotification({
        message: response.error || 'Failed to create HTML Content',
        className: 'bg-admin_error text-white'
      })
      return null
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Current Article Display */}
      <div
        className="flex-1 border-2 border-admin_bordered rounded-lg p-8 flex items-center justify-center cursor-pointer hover:border-admin_primary transition-colors"
        onClick={handleArticleClick}
      >
        {loading ? (
          <Typography variant="admin_txt1_dim">
            Loading...
          </Typography>
        ) : currentArticle ? (
          <div className="text-center">
            <Typography variant="admin_h2">
              {currentArticle.content?.name || currentArticle.content?.id || 'Untitled'}
            </Typography>
          </div>
        ) : (
          <div className="text-center">
            <Typography 
              variant="admin_h3" 
              className="mb-2 text-admin_text_secondary"
            >
              No Article Selected
            </Typography>

            <Typography variant="admin_txt1_dim">
              Click "New" to create or "Load" to select an existing article
            </Typography>
          </div>
        )}
      </div>

      {/* Content Selector Dialog */}
      {showSelector && (
        <ContentSelectorDialog
          type="HTMLContent"
          isOpen={showSelector}
          onClose={() => setShowSelector(false)}
          onSelect={handleSelectArticle}
          parentValues={() => null}
          existingItems={[]}
          field={{}}
        />
      )}

      {/* HTML Editor */}
      {showHtmlEditor && currentArticle && (
        <Modal
          isOpen={showHtmlEditor}
          onClose={() => setShowHtmlEditor(false)}
          closeOnEscape
          variant="admin_fullScreen"
          size="fullscreen"
          styleOverrides={{
            container: "bg-admin_canvas w-full h-full overflow-y-auto",
            contentWrapper: "overflow-y-auto h-[calc(100vh-80px)] scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas",
          }}
        >
          <HtmlEditor
            key={currentArticle._id || currentArticle.content?.id}
            title={currentArticle?.content?.id || 'New HTML Article'}
            content={currentArticle?.content?.content || ''}
            save={handleHtmlEditorSave}
          />
        </Modal>
      )}

      {/* Content Editor for creating new HTMLContent */}
      {showContentEditor && (
        <Modal
          isOpen={showContentEditor}
          onClose={() => setShowContentEditor(false)}
          closeOnEscape
          variant="admin"
          title="Create HTML Content"
          className="w-full sm:w-[500px] md:w-[85%] md:absolute md:top-[15px] h-full md:h-auto"
        >
          {!HTMLContentType ? (
            <div className="p-4 text-center">
              <Typography variant="admin_body" className="text-admin_text">
                Loading content type...
              </Typography>
            </div>
          ) : (
            <ContentEditor
              contentType={HTMLContentType}
              fieldValues={{}}
              onSave={handleContentEditorSave}
              onClickSave={(result) => {
                if (!result) return
                setShowContentEditor(false)
              }}
              onCancel={() => setShowContentEditor(false)}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

// Specify which component this editor is for
export const editorFor = 'HTMLContent'

export default HTMLContentEditor
